import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDocumentForm } from '../contexts/DocumentFormContext';
import { useBatchParsing, ParsedOffer } from '../contexts/BatchParsingContext';
import { emailApiService, EmailMessage, EmailAttachment } from '../services/email/emailApiService';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../foundation/altzero_base/base/components/ui/select';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Checkbox } from '../../../foundation/altzero_base/base/components/ui/checkbox';
import { Loader2, Mail, FileText, ArrowLeft, ArrowRight, Zap, Settings2, Eye, CheckCircle, Upload, X } from 'lucide-react';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';
import { Offer, CONTRACT_LENGTHS, ALL_CONTRACT_TERMS } from '../types/offerTypes';
import { getActiveRetailers } from '../services/retailerService';
import type { Retailer } from '../services/retailerService';
import { generateUUID } from '../utils/cryptoUtils';
import { BatchParsingProgress } from './email/BatchParsingProgress';
import { OfferReviewComparison, OfferReviewComparisonHandle } from './email/OfferReviewComparison';
import { formatTermLabel, formatRateYearLabel } from '../lib/termLabel';
import { TenderTermSeriesPanel } from './email/TenderTermSeriesPanel';
import { termContractStart } from '../lib/termDateRanges';
import { proposeTermSeries } from '../lib/tenderTermSeries';
import {
    applyRetermToParsedData, contractTermsOf, proposeOfferReterm, movesFromTargets, removeTerms,
    type TermMove, type TermTarget,
} from '../lib/bulkReterm';
import type { RetermOfferRow, RetermSelection } from './email/TenderTermSeriesPanel';

interface EmailProcessingStepProps {
    onNext: () => void;
    onPrevious: () => void;
}

// Sentinel value for the "Select Site" dropdown meaning "this offer document covers every
// site in the tender" (a group/bundled quote), as opposed to a specific site's id.
const ALL_SITES_VALUE = '__all_sites__';

const EmailProcessingStep: React.FC<EmailProcessingStepProps> = ({ onNext, onPrevious }) => {
    const {
        sites, tenderId, setOffers, setTenderId, offers, registerStepFlush,
        termSeries, setTermSeries, proposedTermSeries,
    } = useDocumentForm();
    const {
        selectedAttachments,
        assignments,
        parsedOffers,
        isParsing,
        overallProgress,
        addAttachment,
        removeAttachment,
        clearAllAttachments,
        updateAssignment,
        updateBulkAssignment,
        startBatchParsing,
        startSingleParsing,
        retryParsing,
        cancelOffer,
        cancelAllParsing,
        markAsReviewed,
        saveOfferEdits,
        setParsedOffers,
        setSelectedAttachments,
        setAssignments,
    } = useBatchParsing();
    const { toast } = useToast();

    const [emails, setEmails] = useState<EmailMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'assignment' | 'progress' | 'review'>(
        parsedOffers.length > 0 ? 'review' : 'assignment'
    );
    const [bulkSiteId, setBulkSiteId] = useState('');
    const [bulkRetailerId, setBulkRetailerId] = useState('');
    const [bulkTerm, setBulkTerm] = useState('');
    const [reviewOfferIndex, setReviewOfferIndex] = useState(0);

    const [accounts, setAccounts] = useState<any[]>([]);
    const [dbRetailers, setDbRetailers] = useState<Retailer[]>([]);
    const [loadingRetailers, setLoadingRetailers] = useState(false);

    // Manual upload state
    const [manualFiles, setManualFiles] = useState<File[]>([]);
    const [manualRetailerId, setManualRetailerId] = useState('');
    const [uploadingManual, setUploadingManual] = useState(false);
    // Lets "Next" (below) capture whatever's currently being edited in the review screen even
    // if the user never clicked that screen's own "Save & Approve" — without this, a value
    // changed there was invisible to the reconstruction "Next" runs (it only ever saw an
    // approved offer's editedData, never an in-progress, unapproved one).
    const reviewRef = useRef<OfferReviewComparisonHandle>(null);

    // Same reason as above, for the top stepper: clicking another step leaves this screen without
    // ever touching "Next"/"Finalize", so the in-progress edit has to be flushed here or it dies
    // with the unmount. Step 3's saveCurrentStepData payload is only `{ completed: true }`, so
    // there is nothing else that would recover it.
    useEffect(() => registerStepFlush(3, async () => {
        await reviewRef.current?.flushCurrentEdit();
    }), [registerStepFlush]);

    /**
     * Every term each parsed offer quotes, and the contract's first day.
     *
     * Read from editedData first so a re-term the reviewer just made is reflected immediately —
     * the mismatch warnings below have to clear as soon as the offer is fixed, not on reload.
     * Terms come from the contract_terms KEYS, which carry a `28_smooth`-style suffix, so the
     * month count is the numeric prefix and nothing else.
     */
    const parsedOfferTermSummary = useMemo(() => {
        const perOffer: { offerId: string; retailer: string; terms: number[] }[] = [];
        let earliestStart: Date | null = null;
        let latestStart: Date | null = null;
        const startsByRetailer = new Map<string, string>();

        for (const po of parsedOffers) {
            const data = po.editedData || po.parsedData;
            const contractTerms = data?.contract_terms || data?.contractTerms;
            if (!contractTerms || typeof contractTerms !== 'object') continue;

            const terms: number[] = [];
            let offerStart: Date | null = null;
            for (const [key, termData] of Object.entries(contractTerms)) {
                const months = parseInt(String(key), 10);
                if (Number.isFinite(months) && months > 0 && !terms.includes(months)) terms.push(months);

                // The contract start is a value the offers genuinely carry — taken, never
                // invented, so a tender whose offers quote no dates simply shows no span badge.
                const start = termContractStart(termData as Record<string, any>);
                if (!start) continue;
                if (!earliestStart || start < earliestStart) earliestStart = start;
                if (!latestStart || start > latestStart) latestStart = start;
                if (!offerStart || start < offerStart) offerStart = start;
            }
            if (terms.length === 0) continue;

            if (offerStart) {
                startsByRetailer.set(
                    dbRetailers.find(r => r.id === po.assignment?.retailerId)?.name || po.fileName,
                    offerStart.toISOString().slice(0, 10),
                );
            }

            perOffer.push({
                offerId: po.id,
                retailer: dbRetailers.find(r => r.id === po.assignment?.retailerId)?.name
                    || po.fileName,
                terms: terms.sort((a, b) => a - b),
            });
        }

        /**
         * ⚠️ THE EXPIRY WINS. THE OFFERS' QUOTED DATES ARE ONLY THE FALLBACK.
         *
         * This panel used to read `earliestStart` alone and never looked at the site at all,
         * so it reported a start the engine had not used.
         *
         * FORM STRUCTURES PTY LTD: current_contract_expiry 2027-09-01, so the tender starts
         * 2027-09-02 — and five of its six retailers quoted exactly that. Only Engie quoted
         * 2027-09-01. The panel took Engie's, showed "measured from 2027-09-01", counted the
         * 16-month term at 486 days, and told the operator to go and set an expiry that was
         * already set. Meanwhile cost_data carried term_weight 487/365, which decomposes as
         * 365 + 122 from 2027-09-02 — the server had the right date the whole time.
         *
         * Reported as "the system used incorrect start date that should be 02/09/2027". The
         * dates in the numbers were right; this banner was wrong about them.
         *
         * Mirrors the server exactly — resolveContractStartDate (expiry + 1 day) preferred,
         * tenderContractStartFromOffers as fallback, and Math.min across sites when several
         * carry an expiry. See calculationRoutes.ts resolveContractStartDate /
         * tenderWindowFromOffers.
         */
        const expiryStarts: number[] = [];
        for (const site of sites) {
            const raw = (site as { currentContractExpiry?: unknown; current_contract_expiry?: unknown })
                ?.currentContractExpiry
                ?? (site as { current_contract_expiry?: unknown })?.current_contract_expiry;
            if (!raw) continue;
            const ms = raw instanceof Date
                ? Date.UTC(raw.getUTCFullYear(), raw.getUTCMonth(), raw.getUTCDate())
                : Date.parse(`${String(raw).slice(0, 10)}T00:00:00Z`);
            if (Number.isNaN(ms)) continue;
            expiryStarts.push(ms + 86_400_000);   // the day AFTER the old contract ends
        }
        const derivedStart = expiryStarts.length > 0 ? new Date(Math.min(...expiryStarts)) : null;
        const resolvedStart = derivedStart ?? earliestStart;

        return {
            perOffer,
            contractStart: resolvedStart ? resolvedStart.toISOString().slice(0, 10) : null,
            /**
             * The retailers do not agree on when supply begins.
             *
             * ⚠️ THE FALLBACK START IS ONE RETAILER'S OPINION, AND IT MULTIPLIES EVERY DOLLAR.
             *
             * The tender's real start is the day after the current contract expires. When that
             * expiry is blank the window falls back to the EARLIEST date the offers themselves
             * quote, which is only right if the retailers agree — and they often do not.
             *
             * COWES BAKEHOUSE expired 09/04/2026, so the tender began 10/04/2026 and Year 1 ran
             * 355 days to 31/03/2027. Energy Australia and Momentum quoted from 01/04, Flo from
             * 08/04, AGL from 09/04 and only Origin from 10/04. The earliest won, Year 1 became
             * 364 days, and every figure on every retailer and every term printed 2.535% high —
             * Momentum's twelve-month commodity spend $11,411 against the client's $11,129.
             *
             * It stayed invisible because it only bites when they disagree: on the 4/16/28/40 and
             * 10/22/34/46 tenders every offer quoted a single start, so the fallback happened to
             * land on the right day and the same missing expiry cost nothing.
             */
            startsByRetailer: [...startsByRetailer.entries()]
                .map(([retailer, start]) => ({ retailer, start }))
                .sort((a, b) => a.start.localeCompare(b.start)),
            /**
             * Only a disagreement NOBODY HAS SETTLED is worth shouting about.
             *
             * With an expiry on the site the start is already decided and the retailers'
             * differing opinions no longer affect anything, so the red banner and the
             * "start dates differ" badge would be pure noise — and worse, they told the
             * operator to set an expiry that was already set, which is what made a correct
             * tender look broken.
             */
            startsDisagree: derivedStart === null
                && earliestStart !== null && latestStart !== null
                && earliestStart.getTime() !== latestStart.getTime(),
        };
    }, [parsedOffers, dbRetailers, sites]);

    /**
     * The series to offer as a seed.
     *
     * Derived from the offers ON THIS SCREEN first: the parsed documents are what the reviewer
     * is looking at, and the tender's saved offer rows are not rewritten from them until this
     * step is approved — seeding off those would propose the previous run's term set. Falls back
     * to the tender-level proposal for a tender re-opened before anything was re-parsed.
     */
    const suggestedTermSeries = useMemo(() => {
        const fromScreen = proposeTermSeries(parsedOfferTermSummary.perOffer.map(o => o.terms));
        if (Object.keys(fromScreen).length > 0) return fromScreen;
        return proposedTermSeries?.() ?? {};
    }, [parsedOfferTermSummary, proposedTermSeries]);

    /**
     * What each offer's terms would become under this tender's series — a suggestion per term,
     * every one of them changeable.
     *
     * ⚠️ THIS REPLACED A CONTROL THAT HID ITSELF EXACTLY WHEN IT WAS NEEDED.
     *
     * planOfferReterm answered one question — is this ladder UNIFORMLY wrong? — and showed
     * nothing otherwise. EXCH asked for 10 / 22 / 34 / 46; Energy Australia answered
     * 12 / 24 / 36 / 46 and Shell 12 / 24 / 36. The 46 already fits, and the two-month tolerance
     * calls 12≈10, 24≈22 and 36≈34 a fit too, so nothing was offered at all — no bulk plan, no
     * gap row, not even a badge.
     *
     * The money says otherwise. A whole-year term is deliberately never re-weighted onto a
     * non-anniversary window (see termWeightForWindow), so those six terms were priced over
     * their own 365-day years while the tender's Year 1 is 305 days: Energy Australia's Year-1
     * page read $25,093 against the client's $21,026 and Shell's $31,134 against $26,180, while
     * AGL, Origin, Momentum and Flo — who quoted the tender's own lengths — matched to the
     * dollar on every term. Re-termed, all six reproduce the client exactly.
     *
     * So every offer is listed with a per-term suggestion, pre-ticked only where something would
     * actually move. Flo's rule is unchanged and now structural rather than a refusal: a term
     * longer than the series' last year is suggested as KEEP, never as a move and never as a
     * removal, so 52 → 4 cannot happen by itself. The reviewer can still decide anything.
     */
    const retermOffers = useMemo<RetermOfferRow[]>(() => {
        const out: RetermOfferRow[] = [];
        for (const po of parsedOffers) {
            const data = po.editedData || po.parsedData;
            const found = contractTermsOf(data);
            if (!found) continue;
            const proposal = proposeOfferReterm(Object.keys(found.terms), termSeries);
            if (proposal.terms.length === 0) continue;
            out.push({
                offerId: po.id,
                retailer: dbRetailers.find(r => r.id === po.assignment?.retailerId)?.name || po.fileName,
                terms: proposal.terms.map(t => ({
                    fromKey: t.fromKey,
                    fromMonths: t.fromMonths,
                    suffix: t.suffix,
                    suggested: t.suggested,
                    alreadyCorrect: t.alreadyCorrect,
                })),
                suggestSelected: proposal.suggestSelected,
            });
        }
        return out;
    }, [parsedOffers, termSeries, dbRetailers]);

    /**
     * Apply every plan in one pass.
     *
     * Writes to parsedData AND editedData: the review screen seeds its editor from editedData
     * when the offer has been reviewed and from parsedData when it has not, so touching only one
     * of them would leave half the offers looking unchanged. The result then persists through
     * the normal Approve flow, exactly like a manual re-term — nothing is written behind the
     * reviewer's back.
     */
    /**
     * Move ONE term of ONE offer onto a year's month count.
     *
     * For the retailer that answered with a length between two of the tender's years — Next
     * Business's 24 months against a 16-month Year 2 and a 28-month Year 3. Nothing can decide
     * that automatically, so the reviewer picks the year and this performs the same rewrite the
     * per-offer term editor would: key renamed, rates untouched, date range rebased.
     */
    const handleRetermSingleTerm = (offerId: string, fromMonths: number, toMonths: number) => {
        setParsedOffers(prev => prev.map(po => {
            if (po.id !== offerId) return po;

            const build = (data: any): TermMove[] => {
                const found = contractTermsOf(data);
                if (!found) return [];
                return Object.keys(found.terms)
                    .filter(key => parseInt(key, 10) === fromMonths)
                    .map(key => ({
                        fromKey: key,
                        toKey: `${toMonths}${key.indexOf('_') >= 0 ? key.slice(key.indexOf('_')) : ''}`,
                        fromMonths,
                        toMonths,
                    }));
            };

            return {
                ...po,
                parsedData: po.parsedData
                    ? applyRetermToParsedData(po.parsedData, build(po.parsedData))
                    : po.parsedData,
                editedData: po.editedData
                    ? applyRetermToParsedData(po.editedData, build(po.editedData))
                    : po.editedData,
            };
        }));

        toast({
            title: `Term re-termed to ${toMonths} months`,
            description: 'Rates unchanged; the date range was rebased onto the new length.'
                + ' Approve the offer to save.',
            duration: 5000,
        });
    };

    /**
     * Apply exactly what the reviewer ticked — the moves AND the removals, offer by offer.
     *
     * Both halves go through the same parsedData/editedData pair as a hand edit, because the
     * review screen seeds its editor from editedData once an offer has been looked at and from
     * parsedData before that; writing one and not the other leaves half the list looking
     * untouched. Nothing is saved here — Approve still does that.
     */
    const handleApplyReterm = async (selections: RetermSelection[]) => {
        // Apply is pressable at zero on purpose — see the button. Say why rather than doing
        // nothing, which is how this looked like a dead control for a week.
        if (selections.length === 0) {
            toast({
                variant: 'destructive',
                title: 'Nothing was re-termed',
                description: 'No offer is ticked, or every term already sits on its year. '
                    + 'Tick an offer and give one of its lengths a different year.',
                duration: 8000,
            });
            return;
        }

        /**
         * ⚠️ TAKE THE REVIEW PANEL'S IN-PROGRESS EDIT FIRST, OR RE-TERM AND EDIT FIGHT.
         *
         * OfferReviewComparison keeps the offer being viewed in its OWN editedData state and only
         * hands it back on a flush. Re-terming without asking for it first reads the older copy
         * from context, rewrites that, and the panel's next flush — navigating to another
         * retailer, Save & Approve, leaving the step — writes its stale copy straight back over
         * the re-term, in context and in parsed_offer_document both. The reviewer sees the term
         * move and then return.
         *
         * flushEdit is the same call every other path out of that panel already makes, so this
         * adds no new kind of write. Its result lands in context before the rewrite below reads
         * it, because the rewrite uses the offer objects this closure re-reads after the await.
         */
        const flushed = await reviewRef.current?.flushCurrentEdit();

        const rewriteFor = (terms: RetermSelection['terms']) => {
            const { moves, removeKeys } = movesFromTargets(terms);
            if (moves.length === 0 && removeKeys.length === 0) return null;
            return (data: any) => {
                if (!data) return data;
                const moved = moves.length > 0 ? applyRetermToParsedData(data, moves) : data;
                if (removeKeys.length === 0) return moved;
                const found = contractTermsOf(moved);
                if (!found) return moved;
                return { ...(moved as any), [found.key]: removeTerms(found.terms, removeKeys) };
            };
        };

        // Computed BEFORE the state update so the same objects can be persisted below —
        // setParsedOffers is async and its result is not readable here.
        const rewritten = new Map<string, { offer: ParsedOffer; parsedData: any; editedData: any }>();
        /** Why an offer produced no rewrite — reported, never swallowed. See the toast below. */
        const skipped: string[] = [];
        for (const selection of selections) {
            const found = parsedOffers.find(o => o.id === selection.offerId);
            if (!found) { skipped.push(`${selection.offerId}: no longer on this screen`); continue; }
            // The flush's own state update may not have landed in this closure yet — see the note
            // at the top. Its value is newer than anything parsedOffers holds for that one offer.
            const offer = flushed && flushed.offerId === found.id
                ? { ...found, editedData: flushed.editedData }
                : found;
            const label = dbRetailers.find(r => r.id === offer.assignment?.retailerId)?.name
                || offer.fileName;
            /**
             * ⚠️ A MOVE ONTO A LENGTH THE OFFER ALREADY QUOTES IS REFUSED, AND WAS REFUSED IN
             * SILENCE.
             *
             * AGL answered 3 / 15 / 27 / 39. Pointing its 3-month term at Year 1 asks for
             * 3_smooth -> 15_smooth, and applyOfferReterm will not do it because 15_smooth is a
             * real term of that offer — overwriting it would replace a year of rates with three
             * months of them. Correct, and completely invisible: Apply ran, AGL did not move,
             * nothing said why, and the row returned to "Keep at 3mo" on the next render.
             */
            const { moves: plannedMoves } = movesFromTargets(selection.terms);
            const existingKeys = new Set(Object.keys(
                contractTermsOf(offer.editedData || offer.parsedData)?.terms ?? {}));
            const blocked = plannedMoves.filter(m => m.toKey !== m.fromKey && existingKeys.has(m.toKey));
            if (blocked.length > 0) {
                skipped.push(`${label}: ${blocked
                    .map(m => `${m.fromMonths}mo cannot become ${m.toMonths}mo — it already quotes one`)
                    .join(', ')}`);
            }

            const rewrite = rewriteFor(selection.terms);
            if (!rewrite) {
                // movesFromTargets found nothing to do: every length already sits on its year, or
                // two of them asked for the same year and it refused to drop one silently.
                const wanted = selection.terms
                    .filter(t => t.target?.kind === 'year' && t.target.months !== t.fromMonths)
                    .map(t => `${t.fromMonths}mo -> Year ${(t.target as any).year}`);
                skipped.push(wanted.length > 0
                    ? `${label}: ${wanted.join(', ')} could not be applied — that year is already `
                      + `another of its terms`
                    : `${label}: nothing to change`);
                continue;
            }
            const nextParsed = rewrite(offer.parsedData);
            const nextEdited = rewrite(offer.editedData);
            // ⚠️ COMPARE THE KEYS, NOT THE OBJECTS. applyRetermToParsedData spreads a fresh
            // object every time, so identity always differs even when every move was refused.
            // Every move blocked and nothing removed leaves the same term keys behind, and
            // reporting that as saved would be a lie — the reason is already in `skipped`.
            const keysOf = (d: any) => Object.keys(contractTermsOf(d)?.terms ?? {}).sort().join(',');
            if (keysOf(nextParsed) === keysOf(offer.parsedData)
                && keysOf(nextEdited) === keysOf(offer.editedData)) continue;
            rewritten.set(offer.id, { offer, parsedData: nextParsed, editedData: nextEdited });
        }

        /**
         * ⚠️ APPLY USED TO RETURN IN SILENCE.
         *
         * `if (rewritten.size === 0) return;` — the button ran, the panel did not move, and
         * nothing said why. Every reason it can happen is invisible from the screen: the year
         * asked for is already another of that offer's own lengths, the offer is no longer in
         * the list, or there was genuinely nothing to change. Testers read it as a dead button
         * and re-pressed it.
         */
        if (rewritten.size === 0) {
            toast({
                variant: 'destructive',
                title: 'Nothing was re-termed',
                description: skipped.length > 0
                    ? skipped.join('. ')
                    : 'No offer was selected, or every term already sits on its year.',
                duration: 10000,
            });
            return;
        }

        setParsedOffers(prev => prev.map(po => {
            const next = rewritten.get(po.id);
            return next ? { ...po, parsedData: next.parsedData, editedData: next.editedData } : po;
        }));

        let movedCount = 0;
        let removedCount = 0;
        for (const s of selections) {
            const { moves, removeKeys } = movesFromTargets(s.terms);
            movedCount += moves.length;
            removedCount += removeKeys.length;
        }
        toast({
            title: `Re-termed ${rewritten.size} offer${rewritten.size > 1 ? 's' : ''}`,
            description: `${movedCount} term${movedCount === 1 ? '' : 's'} moved onto this tender's years`
                + (removedCount > 0 ? `, ${removedCount} removed` : '')
                + '. Rates unchanged; date ranges rebased. Saving…',
            duration: 5000,
        });

        /**
         * ⚠️ RE-TERMING USED TO CHANGE NOTHING OUTSIDE THIS BROWSER TAB.
         *
         * This wrote to React state and stopped there. Persisting was left to the reviewer
         * re-approving every affected offer afterwards — a step stated only in a toast, and one
         * nobody performs, because the offers were approved BEFORE the series was set and the
         * screen gives no sign they need approving again.
         *
         * A tender asking 4/16/28/40 had ALINTA, Momentum and Engie all answering 12/24/36. They
         * were re-termed on screen and the reviewer moved on. In the database ALINTA's document
         * still read 12/24/36 — its row had last been written by an approval three minutes
         * earlier — and Engie's and Momentum's had never been rewritten at all. All three were
         * then absent from every table of the generated report.
         *
         * So the write happens here, on the click that asked for it:
         *
         *   - saveParsedDocument writes the re-termed data to parsed_offer_document, the same
         *     call navigating between offers already makes, so this is not a new kind of write.
         *   - createOfferFromParsedDoc pushes it into the OFFER row — which is what Compare &
         *     Select, the calculations and the document all read — but ONLY for an offer the
         *     reviewer has already approved. Calling it for an unapproved one would shove it
         *     into Compare & Select early, which is exactly what onSaveEdits refuses to do.
         *
         * A failure is reported, never swallowed: a silent half-save is how this started.
         */
        if (!tenderId) {
            toast({
                variant: 'destructive',
                title: 'Re-term not saved',
                description: 'This tender has no id yet. Approve each offer to save the change.',
            });
            return;
        }

        const failed: string[] = [];
        for (const { offer, parsedData, editedData } of rewritten.values()) {
            const dataToSave = editedData || parsedData;
            if (!dataToSave) continue;
            try {
                const savedDoc = await emailApiService.saveParsedDocument({
                    attachmentId: offer.attachmentId,
                    tenderId,
                    retailerId: offer.assignment.retailerId,
                    tenderSiteId: offer.assignment.siteId,
                    parsedData: dataToSave,
                    confidenceScore: null,
                    fileName: offer.fileName,
                });
                /**
                 * ⚠️ THE OFFER ROW IS WHAT EVERYTHING BELOW THIS SCREEN READS.
                 *
                 * This used to run only for an approved offer, so a re-term on an unapproved one
                 * stopped at parsed_offer_document. On dev tender "choiceProposal ghgtes" AGL's
                 * document was rewritten four times between 19:21 and 20:06 while its offer row
                 * sat untouched from 18:25 — the panel moved and the calculations, Compare &
                 * Select and the document all kept the old terms.
                 *
                 * updateOnly keeps the reason the guard existed: an offer row that exists is kept
                 * truthful, and one that does not is still not created here. Approving remains
                 * the only thing that puts an offer into Compare & Select.
                 */
                if (savedDoc?.id) {
                    await emailApiService.createOfferFromParsedDoc(savedDoc.id, { updateOnly: true });
                }
            } catch (error) {
                console.error(`Failed to persist re-term for ${offer.fileName}:`, error);
                failed.push(
                    dbRetailers.find(r => r.id === offer.assignment?.retailerId)?.name || offer.fileName,
                );
            }
        }

        if (failed.length > 0) {
            toast({
                variant: 'destructive',
                title: `${failed.length} re-term${failed.length > 1 ? 's' : ''} not saved`,
                description: `${failed.join(', ')} could not be written. Re-approve ${
                    failed.length > 1 ? 'those offers' : 'that offer'} to save the change.`,
                duration: 12000,
            });
        } else {
            toast({
                title: 'Re-term saved',
                description: `${rewritten.size} offer${rewritten.size > 1 ? 's' : ''} updated. `
                    + 'Recalculate to refresh the totals.',
                duration: 6000,
            });
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                // Load metadata in parallel, but handle individual failures
                await Promise.allSettled([
                    loadEmails(),
                    loadAccounts(),
                    loadDbRetailers()
                ]);
            } catch (err) {
                console.warn('Metadata loading partially failed', err);
            }

            // If we have no active parsing batch, check if there are already parsed documents in the DB
            if (parsedOffers.length === 0 && tenderId) {
                try {
                    console.log('🔍 Checking for existing parsed documents in DB...');
                    const docs = await emailApiService.getParsedDocuments(tenderId);

                    if (docs && docs.length > 0) {
                        console.log(`✅ Found ${docs.length} existing parsed documents`);

                        const restoredSelected = new Map();
                        const restoredAssignments = new Map();

                        const restoredOffers: ParsedOffer[] = docs.map((doc: any) => {
                            if (doc.attachment_id) {
                                restoredSelected.set(doc.attachment_id, {
                                    attachment: doc.attachment,
                                    emailId: doc.attachment?.message_id || '',
                                    accountId: ''
                                });
                                restoredAssignments.set(doc.attachment_id, {
                                    siteId: doc.tender_site_id || '',
                                    retailerId: doc.retailer_id || '',
                                    termMonths: 12
                                });
                            }

                            return {
                                id: `restored_${doc.id}`,
                                attachmentId: doc.attachment_id || '',
                                accountId: '',
                                fileName: doc.attachment?.filename || 'Parsed Document',
                                emailId: doc.attachment?.message_id || '',
                                tenderId: doc.tender_id,
                                assignment: {
                                    siteId: doc.tender_site_id || '',
                                    retailerId: doc.retailer_id || '',
                                    termMonths: 12
                                },
                                status: 'reviewed',
                                progress: 100,
                                parsedData: doc.parsed_data,
                                createdAt: new Date(doc.created_at),
                            };
                        });

                        setParsedOffers(restoredOffers);
                        setSelectedAttachments(restoredSelected);
                        setAssignments(restoredAssignments);
                        setViewMode('review');
                    }
                } catch (error) {
                    console.error('Failed to load existing parsed docs:', error);
                }
            }
            setLoading(false);
        };

        loadInitialData();
    }, [tenderId]);

    // Auto-switch view mode based on parsing status changes
    const parsingStatusKey = parsedOffers.map(o => o.status).join(',');
    useEffect(() => {
        if (parsedOffers.length === 0) return;
        const allFinished = parsedOffers.every(o => o.status === 'completed' || o.status === 'reviewed' || o.status === 'error');
        if (allFinished) {
            setViewMode('review');
        } else if (viewMode === 'assignment') {
            setViewMode('progress');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsingStatusKey]);

    const loadAccounts = async () => {
        try {
            const accs = await emailApiService.getAccounts();
            setAccounts(accs);
        } catch (error) {
            console.error('Failed to load accounts:', error);
        }
    };

    const loadEmails = async () => {
        if (!tenderId) return;
        setLoading(true);
        try {
            const msgs = await emailApiService.getInboundMessages({ tenderId, hasAttachments: true });
            // Filter out automated bounce / delivery-failure notifications
            const BOUNCE_PATTERNS = [
                /mailer-daemon/i,
                /mail.*delivery.*subsystem/i,
                /postmaster/i,
                /no-?reply@.*google/i,
                /delivery.*status.*notification/i,
                /undelivered.*mail/i,
            ];
            const filtered = msgs.filter(m => {
                const sender = (m.sender_email || '').toLowerCase();
                return !BOUNCE_PATTERNS.some(p => p.test(sender));
            });
            setEmails(filtered);
        } catch (error) {
            console.error('Failed to load emails:', error);
            toast({
                title: 'Error',
                description: 'Failed to load inbound emails',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const loadDbRetailers = async () => {
        try {
            setLoadingRetailers(true);
            const result = await getActiveRetailers();
            if (result.success && result.data) {
                setDbRetailers(result.data);
            } else {
                console.error('Failed to load retailers:', result.error);
                setDbRetailers([]);
            }
        } catch (error) {
            console.error('Failed to load database retailers:', error);
            setDbRetailers([]);
        } finally {
            setLoadingRetailers(false);
        }
    };

    // Match sender email / subject / filename to a retailer
    // filename param is optional — pass attachment.filename for Tier 6 matching
    const getRetailerForEmail = (senderEmail: string, subject: string, filename?: string): Retailer | null => {
        if (!dbRetailers.length) return null;

        // Extract plain email address from "Display Name <email@domain.com>" format
        const angleMatch = senderEmail.match(/<([^>]+)>/);
        const plainEmail = angleMatch ? angleMatch[1].trim() : senderEmail.trim();

        const senderDomain = plainEmail.split('@')[1]?.toLowerCase() || '';
        const senderLower = plainEmail.toLowerCase();

        // Tier 1: exact match in contact_emails array (highest confidence)
        for (const r of dbRetailers) {
            if (r.contact_emails?.some(e => e.toLowerCase() === senderLower)) {
                return r;
            }
        }
        // Tier 2: domain match in email_domains array
        // Guard: skip TLD-only or SLD-only entries (e.g. 'au', 'com.au') that would
        // match every Australian email address and always return the first retailer.
        for (const r of dbRetailers) {
            if (r.email_domains?.some(d => {
                const dLower = d.replace(/^@/, '').toLowerCase();
                if (!dLower) return false;
                const dotCount = (dLower.match(/\./g) || []).length;
                // Require at least 1 dot; for .au domains require 2 dots (company.sld.au)
                if (dotCount < 1) return false;
                if (dLower.endsWith('.au') && dotCount < 2) return false;
                return senderDomain === dLower || senderDomain.endsWith('.' + dLower);
            })) {
                return r;
            }
        }
        // Tier 3: domain match from contact_email (single field)
        // Skip free/shared email providers — they are not company-specific and would
        // match every email sent from Gmail/Outlook, always returning the first retailer.
        const FREE_EMAIL_DOMAINS = new Set([
            'gmail.com', 'googlemail.com',
            'outlook.com', 'outlook.com.au', 'hotmail.com', 'hotmail.com.au', 'live.com',
            'yahoo.com', 'yahoo.com.au', 'icloud.com', 'me.com',
        ]);
        for (const r of dbRetailers) {
            if (r.contact_email) {
                const rDomain = r.contact_email.split('@')[1]?.toLowerCase() || '';
                if (rDomain && !FREE_EMAIL_DOMAINS.has(rDomain) && senderDomain === rDomain) return r;
            }
        }
        // Tier 4: domain match from contact_emails array
        // Entries can be full emails ("user@agl.com.au") OR bare domains ("gmail.com")
        for (const r of dbRetailers) {
            if (r.contact_emails?.some(e => {
                const eLower = e.toLowerCase().trim();
                // Bare domain entry (no @): compare directly with senderDomain
                if (!eLower.includes('@')) {
                    return senderDomain === eLower || senderDomain.endsWith('.' + eLower);
                }
                // Full email entry: extract and compare domain
                const d = eLower.split('@')[1];
                return d && senderDomain === d;
            })) {
                return r;
            }
        }
        // Tier 5: retailer name substring match in sender display-name or subject
        const senderFull = senderEmail.toLowerCase();
        for (const r of dbRetailers) {
            const nameLower = r.name.toLowerCase();
            if (nameLower.length > 2 && (
                senderFull.includes(nameLower) ||
                subject.toLowerCase().includes(nameLower)
            )) {
                return r;
            }
        }
        // Tier 6: retailer name substring match in the attachment filename
        // This catches cases like "COWES BAKEHOUSE PTY. LTD.-AYAA-6SACJU.pdf"
        // where the retailer name is embedded directly in the filename.
        if (filename) {
            const filenameLower = filename.toLowerCase()
                // Strip common separators so "COWES_BAKEHOUSE" still matches "cowes bakehouse"
                .replace(/[-_]/g, ' ');
            for (const r of dbRetailers) {
                const nameLower = r.name.toLowerCase().replace(/[-_]/g, ' ');
                // Require at least 4 chars to avoid false-positives on short names
                if (nameLower.length > 3 && filenameLower.includes(nameLower)) {
                    return r;
                }
            }
        }
        return null;
    };

    const handleAttachmentToggle = (email: EmailMessage, attachment: EmailAttachment, checked: boolean) => {
        if (checked) {
            // Use email.account_id if available, otherwise fallback to the first active account
            const accountId = email.account_id || accounts.find(a => a.is_active)?.id || '';

            if (!accountId) {
                console.warn('No account ID found for attachment download');
            }

            addAttachment(attachment.id, attachment, accountId);

            // Auto-detect retailer from sender email domain/name, or from the attachment filename (Tier 6)
            const retailer = getRetailerForEmail(email.sender_email, email.subject, attachment.filename);

            if (retailer) {
                // Auto-select retailer when matched, but leave site unset — silently defaulting
                // to sites[0] meant multi-site tenders got the offer attached to the wrong single
                // site without the user noticing. Force an explicit Site choice every time
                // (including "All Sites" for group offers).
                updateAssignment(attachment.id, {
                    siteId: '',
                    retailerId: retailer.id,
                    termMonths: 12
                });
            }
        } else {
            removeAttachment(attachment.id);
        }
    };

    const handleApplyBulkAssignment = () => {
        const bulkUpdate: any = {};
        if (bulkSiteId) bulkUpdate.siteId = bulkSiteId;
        if (bulkRetailerId) bulkUpdate.retailerId = bulkRetailerId;
        if (bulkTerm) bulkUpdate.termMonths = parseInt(bulkTerm);

        if (Object.keys(bulkUpdate).length === 0) {
            toast({
                title: 'No Selection',
                description: 'Please select at least one field to apply',
                variant: 'destructive',
            });
            return;
        }

        updateBulkAssignment(bulkUpdate);
        toast({
            title: 'Applied',
            description: `Bulk assignment applied to ${selectedAttachments.size} attachment(s)`,
        });
    };

    const handleManualUpload = async () => {
        if (manualFiles.length === 0) return;
        if (!tenderId) {
            toast({ title: 'Error', description: 'No tender context', variant: 'destructive' });
            return;
        }
        setUploadingManual(true);
        let uploaded = 0;
        for (const file of manualFiles) {
            try {
                const att = await emailApiService.uploadManualAttachment(file, tenderId, manualRetailerId || undefined);
                const accountId = accounts.find(a => a.is_active)?.id || '';
                addAttachment(att.id, att, accountId);
                updateAssignment(att.id, {
                    // Leave site unset — force explicit choice (see comment above the other
                    // updateAssignment call for why silently defaulting to sites[0] is wrong).
                    siteId: '',
                    retailerId: manualRetailerId || '',
                    termMonths: 12,
                });
                uploaded++;
            } catch (err: any) {
                toast({ title: `Failed to upload ${file.name}`, description: err.message, variant: 'destructive' });
            }
        }
        if (uploaded > 0) {
            toast({ title: 'Uploaded', description: `${uploaded} file(s) ready for parsing` });
            setManualFiles([]);
            setManualRetailerId('');
        }
        setUploadingManual(false);
    };

    const handleStartParsing = async () => {
        // Validate all attachments have complete assignments
        const incompleteAttachments: string[] = [];
        selectedAttachments.forEach((value, attachmentId) => {
            const assignment = assignments.get(attachmentId);
            if (!assignment || !assignment.siteId || !assignment.retailerId) {
                incompleteAttachments.push(value.attachment.filename);
            }
        });

        if (incompleteAttachments.length > 0) {
            toast({
                title: 'Incomplete Assignments',
                description: `Please assign Site and Retailer for all attachments`,
                variant: 'destructive',
            });
            return;
        }

        // CRITICAL FIX: Pass tender_id when parsing
        if (!tenderId) {
            toast({
                title: 'Error',
                description: 'No tender selected. Please ensure you have a tender context.',
                variant: 'destructive',
            });
            return;
        }

        await startBatchParsing(tenderId);
        toast({
            title: 'Parsing Started',
            description: `Parsing ${selectedAttachments.size} offer(s)...`,
        });
    };

    const canProceedToNext = () => {
        // Allow proceeding if at least one offer has been reviewed
        return parsedOffers.length > 0 && parsedOffers.some(o => o.status === 'reviewed');
    };


    // latestFlushedEdit: the currently-viewed offer's just-flushed edit (from clicking "Next"
    // directly, without "Save & Approve" first) — parsedOffers here is a React closure captured
    // BEFORE that flush's markAsReviewed/setParsedOffers state update landed, so po.editedData
    // for that ONE offer would still be stale even though the flush's own DB write already
    // completed. This override lets the reconstruction below use the correct, just-flushed value
    // for that specific offer without waiting on (or depending on) the closure ever refreshing.
    const handleProceedToOffers = (latestFlushedEdit?: { offerId: string; editedData: any }) => {
        // Convert parsed offers to Offer format for DocumentFormContext
        const convertedOffers: Offer[] = [];

        console.log('🔍 TENDER_ID DEBUG - Current context tender_id:', tenderId);
        console.log('🔍 TENDER_ID DEBUG - Parsed offers tender_ids:', parsedOffers.map(po => ({ id: po.id, tenderId: po.tenderId })));

        parsedOffers.forEach(po => {
            // Only process offers that have been successfully parsed or reviewed
            if (!po.parsedData && !po.editedData) {
                return;
            }

            try {
                // CRITICAL: Validate tender_id match - parsed offer's tender_id MUST match context
                if (po.tenderId && po.tenderId !== tenderId) {
                    console.error(`❌ TENDER_ID MISMATCH! Parsed offer ${po.id} has tender_id ${po.tenderId}, but context has ${tenderId}`);
                    console.error(`   This is a critical bug - offers will be created with WRONG tender_id!`);
                    console.error(`   Fixing by using context tender_id: ${tenderId}`);
                }

                // Use editedData if available (user reviewed), otherwise parsedData — unless this
                // is the offer that was just flushed, in which case that value is newer than
                // whatever this closure's po.editedData currently holds.
                const data = (latestFlushedEdit && po.id === latestFlushedEdit.offerId)
                    ? latestFlushedEdit.editedData
                    : (po.editedData || po.parsedData);

                // Get assignment data (source of truth for site/retailer)
                const assignment = po.assignment;

                // Resolve which site(s) this offer applies to. ALL_SITES_VALUE means the source
                // document is a group/bundled quote covering every site in the tender (e.g. one
                // Origin/EA/Momentum contract listing 3 NMIs) — build one Offer per site instead
                // of forcing everything onto whichever single site happened to be selected.
                const targetSites = assignment.siteId === ALL_SITES_VALUE
                    ? sites
                    : sites.filter(s => s.id === assignment.siteId);

                if (targetSites.length === 0) {
                    console.error(`❌ No site(s) resolved for assignment siteId=${assignment.siteId} on offer ${po.fileName}. Skipping.`);
                    return;
                }

                // assignment.retailerId must be a real database retailer UUID
                const dbRetailer = dbRetailers.find(r => r.id === assignment.retailerId);
                if (!dbRetailer) {
                    console.error(`❌ Retailer not found for ID ${assignment.retailerId} on offer ${po.fileName}. Skipping — please assign a valid retailer.`);
                    return;
                }

                // CRITICAL FIX: USE CONTEXT tender_id (from URL), NOT po.tenderId
                // Context tender_id is loaded from URL and is ALWAYS correct
                // po.tenderId might be stale if user resumed a different tender
                if (!tenderId) {
                    console.error(`❌ No tender_id in context! Cannot create offer. This should never happen.`);
                    return; // Skip this offer
                }

                // Extract rates - supports both camelCase and snake_case
                // The LLM returns data with contractTerms/contract_terms for rates and smooth for charges
                // (site-independent — the parsed document has one rate set regardless of how many
                // sites the group offer covers)
                const contractTerms = data.contractTerms || data.contract_terms || {};
                const commonCharges = data.smooth || data.stepped || {};

                // Get available contract terms from parsed data (dynamic terms like 10, 18, 22, 30, 36)
                //
                // ⚠️ Keys are bare ("15") OR suffixed ("15_stepped" / "15_smooth"), and the month
                // count lives in the numeric PREFIX. This used to filter on
                // `!isNaN(Number(key))` — but Number("15_stepped") is NaN, so every suffixed key
                // was dropped, availableTermKeys came out empty and the CONTRACT_LENGTHS fallback
                // fired. That stamped [12,24,36,48,60] onto offers whose real terms were
                // 15/27/39/51 (prod tender fa2a96e5), and everything derived from the same list
                // went with it: termMonths became 24, longestTerm 60, maxYears 5, and the notes
                // line advertised terms the retailer never quoted.
                const termKeyMonths = Object.keys(contractTerms)
                    .map(key => ({ key, months: parseInt(key, 10) }))
                    .filter((e): e is { key: string; months: number } =>
                        Number.isFinite(e.months) && e.months > 0);
                const availableTermKeys = termKeyMonths.map(e => e.key);
                // Deduped: "15_smooth" and "15_stepped" are one 15-month term, not two.
                const termsToProcess = termKeyMonths.length > 0
                    ? [...new Set(termKeyMonths.map(e => e.months))].sort((a, b) => a - b)
                    : [...CONTRACT_LENGTHS]; // Fallback to standard terms if none found

                // CRITICAL FIX: Create ONE offer per retailer (not one per term)
                // Primary term: use 24 months if available, otherwise first term
                const primaryTerm = termsToProcess.includes(24) ? 24 : termsToProcess[0] || 24;

                // Calculate longest term to get all year data
                const longestTerm = Math.max(...termsToProcess);
                const maxYears = Math.ceil(longestTerm / 12);

                // Helper to safely parse rate
                const getRate = (val: any) => {
                    if (typeof val === 'number') return val;
                    if (typeof val === 'string') return parseFloat(val) || 0;
                    return 0;
                };

                // Use longest term data to capture all years.
                // Resolved from the key that actually carries it — a bare String(longestTerm)
                // misses "51_stepped" and silently fell through to Object.values()[0], which is
                // the SHORTEST term (15 months) and holds none of the later years.
                const longestTermKey = termKeyMonths.find(e => e.months === longestTerm)?.key
                    ?? String(longestTerm);
                const termData = contractTerms[longestTermKey] ||
                    Object.values(contractTerms)[0] ||
                    commonCharges;

                if (!termData) {
                    return;
                }

                // Map environmental and non-energy charges from parsed data (site-independent)
                const envCharges = data.environmental_charges || data.environmentalCharges ||
                    commonCharges.environmentalCharges || commonCharges.environmental_charges || {};
                const nonEnergyCharges = data.non_energy_charges || data.nonEnergyCharges ||
                    commonCharges.nonEnergyCharges || commonCharges.non_energy_charges || {};

                console.log(`✅ Creating offer(s) with tender_id: ${tenderId} for ${targetSites.length} site(s) (from context/URL)`);

                // Build one Offer per target site — each gets its own id/offerLines (own DB row),
                // but shares the same parsed rates since a group offer prices identically per site.
                targetSites.forEach(site => {
                    const offerId = generateUUID();
                    const offerLines: any[] = [];
                    let lineId = 0;
                    const yearsIncluded = maxYears;

                    // Determine NSW state once per offer (outside year loop) — evaluated per site
                    // since a group offer's sites could in principle span different states.
                    const siteState = (site?.address?.state || (site as any)?.addr_state || '').toUpperCase();
                    const isNSW = siteState === 'NSW' || siteState === 'NEW SOUTH WALES';

                    for (let year = 1; year <= yearsIncluded; year++) {
                        const yearKey = `year_${year}`;
                        const yearData = termData[yearKey] || (termData.peakRates ? {
                            peak: termData.peakRates[yearKey],
                            offpeak: termData.offpeakRates?.[yearKey],
                            shoulder: termData.shoulderRates?.[yearKey]
                        } : null);

                        if (!yearData) continue;

                        // Peak Rate
                        const peakRate = getRate(yearData.peak);
                        if (peakRate > 0) {
                            offerLines.push({
                                id: `${offerId}_line_${lineId++}`,
                                offerId: offerId,
                                component: 'peak_rate',
                                unit: 'c/kWh',
                                rate: peakRate,
                                isGroupRate: true,
                                notes: formatRateYearLabel(year)
                            });
                        }

                        // Off-peak Rate
                        const offpeakRate = getRate(yearData.offpeak);
                        if (offpeakRate > 0) {
                            offerLines.push({
                                id: `${offerId}_line_${lineId++}`,
                                offerId: offerId,
                                component: 'offpeak_rate',
                                unit: 'c/kWh',
                                rate: offpeakRate,
                                isGroupRate: true,
                                notes: formatRateYearLabel(year)
                            });
                        }

                        // Shoulder Rate - NSW: use peak as shoulder if no shoulder provided
                        let shoulderRate = getRate(yearData.shoulder);
                        if (shoulderRate === 0 || !yearData.shoulder) {
                            shoulderRate = isNSW ? peakRate : 0;
                        }
                        if (shoulderRate > 0) {
                            offerLines.push({
                                id: `${offerId}_line_${lineId++}`,
                                offerId: offerId,
                                component: 'shoulder_rate',
                                unit: 'c/kWh',
                                rate: shoulderRate,
                                isGroupRate: true,
                                notes: formatRateYearLabel(year)
                            });
                        }
                    }

                    // Guard: never create an offer with zero parsed rate lines — this is what
                    // produced "duplicate, no values" entries when a re-parse returned empty/
                    // malformed contract_terms (termData truthy but no usable year_N rates inside).
                    if (offerLines.length === 0) {
                        console.error(`❌ Parsed offer for ${po.fileName} @ ${site.name} produced zero rate lines — skipping instead of creating an empty offer.`);
                        return;
                    }

                    // Derive confidence from how many critical fields were parsed
                    const parsedTermCount = availableTermKeys.length;
                    const hasEnvCharges = Object.keys(envCharges).length > 0;
                    const hasNonEnergy = Object.keys(nonEnergyCharges).length > 0;
                    const parsedConfidence = Math.min(
                        100,
                        (parsedTermCount > 0 ? 40 : 0) +
                        (offerLines.length > 0 ? 30 : 0) +
                        (hasEnvCharges ? 20 : 0) +
                        (hasNonEnergy ? 10 : 0)
                    );

                    const offer: Offer = {
                        id: offerId,
                        orgId: '', // Will be set by backend
                        tenderId: tenderId,
                        tenderSiteId: site.id,
                        siteId: site.id,
                        siteName: site.name,
                        retailerId: dbRetailer.id,
                        retailerName: dbRetailer.name,
                        retailerLogo: dbRetailer.logo || null,
                        offerType: 'electricity',
                        termMonths: primaryTerm,
                        availableTerms: termsToProcess,
                        expiryDate: data.expiry_date || data.expiryDate || null,
                        validityDate: data.validity_date || data.validityDate || null,
                        greenPercent: 0,
                        feesJson: {
                            environmentalCharges: envCharges,
                            nonEnergyCharges: nonEnergyCharges
                        },
                        sourceFileId: po.fileName,
                        confidence: parsedConfidence,
                        isRecommended: false,
                        recommendedMonths: [],
                        offerLines: offerLines,
                        multiYearRateData: {
                            smooth: {
                                peakRates: {}, // Will be populated below (from longest term)
                                offpeakRates: {},
                                shoulderRates: {},
                                environmentalCharges: envCharges,
                                nonEnergyCharges: nonEnergyCharges
                            },
                            // CRITICAL FIX: Store ALL contract terms' rates here (term-specific rates)
                            contract_terms: contractTerms,
                            // The reviewer's term -> year assignment, carried through from review.
                            //
                            // This object is built from scratch rather than spread from `data`, so
                            // anything not named here is dropped — which is exactly what happened:
                            // approving an offer wrote the assignment via createOfferFromParsedDoc,
                            // then Proceed to Offers overwrote the row with a fresh object that had
                            // none. Compare & Select then re-derived, and because the 4- and
                            // 16-month terms both derive to Year 1 the term filter collapsed to
                            // Years 1-4 with the assigned Year 5 nowhere.
                            term_year_labels: data.term_year_labels ?? null,
                        } as any,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        status: 'draft',
                        notes: `Parsed from ${po.fileName} (${termsToProcess.map((t: number) => formatTermLabel(t)).join(', ')} available)`,
                        extn: {
                            parsedOfferId: po.id,
                            contract_terms: contractTerms, // Keep raw terms for calculation
                            // Mirrored beside contract_terms, matching offerAutoPopulateService,
                            // so either read path finds the assignment.
                            term_year_labels: data.term_year_labels ?? null,
                        }
                    };

                    // Populate multiYearRateData with all year rates
                    offerLines.forEach(line => {
                        const yearMatch = line.notes?.match(/Year (\d+)/);
                        const yearKey = yearMatch ? `year_${yearMatch[1]}` : 'year_1';
                        if (line.component === 'peak_rate') offer.multiYearRateData!.smooth.peakRates[yearKey] = line.rate;
                        if (line.component === 'offpeak_rate') offer.multiYearRateData!.smooth.offpeakRates[yearKey] = line.rate;
                        if (line.component === 'shoulder_rate') offer.multiYearRateData!.smooth.shoulderRates[yearKey] = line.rate;
                    });

                    convertedOffers.push(offer);
                });
            } catch (error) {
                console.error('Error converting offer:', error);
            }
        });

        console.log('✅ Converting parsed offers to Offer format:', {
            parsedCount: parsedOffers.length,
            convertedCount: convertedOffers.length,
            offers: convertedOffers
        });

        // Transfer to DocumentFormContext
        const currentContextOffers = offers || []; // Safety fallback

        const finalizedOffers: Offer[] = convertedOffers.map(co => {
            // CRITICAL FIX: To prevent duplicate records in the "offer" table,
            // we MUST check if an offer already exists for this tender/site/retailer.
            // If it does, we preserve its ID so the database "upsert" performs an update.
            const existingOffer = currentContextOffers.find(eo =>
                eo.tenderId === co.tenderId &&
                eo.tenderSiteId === co.tenderSiteId &&
                eo.retailerId === co.retailerId
            );

            if (existingOffer) {
                console.log(`♻️ Re-using existing offer ID ${existingOffer.id} for ${co.retailerName} at ${co.siteName}`);
                return {
                    ...co,
                    id: existingOffer.id, // PERSIST THE ID!!
                    // Preserve some existing fields if needed, but usually we want to replace with newly parsed/reviewed data
                    createdAt: existingOffer.createdAt || co.createdAt
                };
            }
            return co;
        });

        setOffers(finalizedOffers);

        // CRITICAL FIX: Update context tender_id to match offers' tender_id
        // This ensures the context stays in sync with the saved offers
        if (finalizedOffers.length > 0 && finalizedOffers[0].tenderId && finalizedOffers[0].tenderId !== tenderId) {
            const offerTenderId = finalizedOffers[0].tenderId;
            console.log(`🔧 Updating context tender_id from ${tenderId} to ${offerTenderId}`);
            setTenderId(offerTenderId);
        }

        // Also save to session storage as backup/primary transfer mechanism
        sessionStorage.setItem('scopingai_staged_offers_list', JSON.stringify(convertedOffers));

        toast({
            title: 'Offers Loaded',
            description: `${convertedOffers.length} offer(s) ready for review`,
        });

        // ⚠️ THE LAST PLACE THE REVIEWER CAN STILL FIX THIS.
        //
        // An offer quoting lengths the tender's series does not describe contributes nothing to
        // any page: every figure it has is filed under a term no page asks for. The generator
        // already detects it — server/.../scopingAi.ts logs "quotes 12/24/36 months, which the
        // tender's term series does not describe" — but only to the server console, and by then
        // the reviewer is four steps away.
        //
        // A WERRIBEE-shaped tender asking 4/16/28/40 got ALINTA, Momentum and Engie all
        // answering 12/24/36. None was re-termed, nothing on screen said so, and the report
        // shipped with three of its seven retailers absent from every table.
        //
        // Named, not blocked: leaving them as they are is a legitimate choice (a retailer who
        // genuinely only sells whole years may belong nowhere on this tender), so this states
        // the consequence and lets the reviewer decide — which is the same rule the re-term
        // list itself follows.
        const unplaced = retermOffers.filter(o => o.suggestSelected);
        if (unplaced.length > 0) {
            toast({
                variant: 'destructive',
                title: `${unplaced.length} offer${unplaced.length > 1 ? 's' : ''} will be blank in the document`,
                description: `${unplaced.map(o => o.retailer).join(', ')} quote contract lengths this`
                    + ` tender did not ask for, so they appear on no page. Go back to Contract terms`
                    + ` and re-term them, or continue and they will be left out.`,
                duration: 12000,
            });
        }

        // Proceed to next step
        onNext();
    };


    return (
        <div className="flex flex-col space-y-4 h-full min-h-[500px]">
            {/* View Mode Tabs */}
            <div className="flex gap-2">
                <Button
                    variant={viewMode === 'assignment' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('assignment')}
                >
                    <Settings2 className="h-4 w-4 mr-2" />
                    Select Offers
                </Button>
                <Button
                    variant={viewMode === 'progress' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('progress')}
                    disabled={parsedOffers.length === 0}
                >
                    <Loader2 className="h-4 w-4 mr-2" />
                    Progress
                </Button>
                <Button
                    variant={viewMode === 'review' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('review')}
                    disabled={parsedOffers.length === 0}
                >
                    <Eye className="h-4 w-4 mr-2" />
                    Review
                </Button>
            </div>

            {/* Assignment View */}
            {viewMode === 'assignment' && (
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 overflow-y-auto lg:overflow-hidden min-h-0">
                    {/* Left: Email List with inline manual upload */}
                    <Card className="flex flex-col h-full min-h-[400px] lg:min-h-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                Inbound Emails ({emails.length})
                            </CardTitle>
                        </CardHeader>
                        {/* Manual Upload section — collapsible, inside the same card */}
                        <div className="px-4 pb-3 border-b">
                            <div className="rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-700">
                                    <Upload className="h-3.5 w-3.5" />
                                    Upload Offer Manually
                                    <span className="font-normal text-muted-foreground">(no email needed)</span>
                                </div>
                                <div className="flex gap-2 items-center flex-wrap">
                                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 rounded-md cursor-pointer hover:bg-blue-50 text-xs text-blue-700 transition-colors">
                                        <Upload className="h-3 w-3" />
                                        Choose files
                                        <input
                                            type="file"
                                            multiple
                                            accept=".pdf,.xlsx,.xls,.docx,.doc"
                                            className="hidden"
                                            onChange={e => {
                                                if (e.target.files) setManualFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                                                e.target.value = '';
                                            }}
                                        />
                                    </label>
                                    <Select value={manualRetailerId} onValueChange={setManualRetailerId}>
                                        <SelectTrigger className="h-7 text-xs w-36">
                                            <SelectValue placeholder="Retailer..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {dbRetailers.map(r => (
                                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        size="sm"
                                        className="h-7 text-xs px-3"
                                        disabled={manualFiles.length === 0 || uploadingManual}
                                        onClick={handleManualUpload}
                                    >
                                        {uploadingManual ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                        {uploadingManual ? 'Uploading...' : `Upload${manualFiles.length > 0 ? ` (${manualFiles.length})` : ''}`}
                                    </Button>
                                </div>
                                {manualFiles.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {manualFiles.map((f, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white border rounded px-1.5 py-0.5 text-gray-700">
                                                {f.name}
                                                <button onClick={() => setManualFiles(prev => prev.filter((_, j) => j !== i))}>
                                                    <X className="h-2.5 w-2.5 text-gray-400 hover:text-red-500" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <CardContent className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                </div>
                            ) : emails.length === 0 ? (
                                <div className="text-center text-muted-foreground p-4">
                                    No emails with attachments found for this tender.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {emails.map(email => {
                                        // Try sender/subject first; fall back to scanning attachment filenames (Tier 6)
                                        const matchedRetailer = getRetailerForEmail(email.sender_email, email.subject)
                                            ?? email.attachments.reduce<Retailer | null>((found, att) => {
                                                if (found) return found;
                                                return getRetailerForEmail(email.sender_email, email.subject, att.filename);
                                            }, null);
                                        return (
                                            <div key={email.id} className="border rounded-lg p-3 space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="font-medium text-sm truncate">{email.sender_email}</div>
                                                    {matchedRetailer && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                                                            {matchedRetailer.logo && (
                                                                <img src={matchedRetailer.logo} alt="" className="w-3 h-3 rounded-sm object-contain" />
                                                            )}
                                                            {matchedRetailer.name}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{email.subject}</div>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {email.attachments.map(att => (
                                                        <label
                                                            key={att.id}
                                                            className={`flex items-center gap-2 px-3 py-1.5 border rounded-md cursor-pointer transition-colors ${selectedAttachments.has(att.id) ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <Checkbox
                                                                checked={selectedAttachments.has(att.id)}
                                                                onCheckedChange={(checked) =>
                                                                    handleAttachmentToggle(email, att, checked as boolean)
                                                                }
                                                            />
                                                            <FileText className="h-3 w-3 flex-shrink-0" />
                                                            <span className="text-xs truncate max-w-[150px]">
                                                                {att.filename}
                                                            </span>
                                                            {matchedRetailer && (
                                                                <span className="text-[10px] font-medium text-blue-600 flex-shrink-0">
                                                                    {matchedRetailer.name}
                                                                </span>
                                                            )}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Right: Assignment Panel */}
                    <Card className="flex flex-col h-full min-h-[400px] lg:min-h-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings2 className="h-5 w-5" />
                                Offer Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6 overflow-y-auto">
                            {selectedAttachments.size === 0 ? (
                                <div className="text-center text-muted-foreground py-10">
                                    Select attachments from the left to assign details.
                                </div>
                            ) : (
                                <>


                                    {/* Individual Assignments */}
                                    <div className="space-y-3">
                                        <Label className="text-sm font-medium">Offer Assignments</Label>
                                        {Array.from(selectedAttachments.entries()).map(([attachmentId, value]) => {
                                            const assignment = assignments.get(attachmentId) || {
                                                siteId: '',
                                                retailerId: '',
                                                termMonths: 12
                                            };

                                            return (
                                                <div key={attachmentId} className="p-3 border rounded-lg space-y-2">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="text-xs font-medium truncate flex-1">
                                                            {value.attachment.filename}
                                                        </div>
                                                        <button
                                                            onClick={() => removeAttachment(attachmentId)}
                                                            className="text-gray-400 hover:text-red-500 shrink-0"
                                                            title="Remove this offer"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <Select
                                                                value={assignment.siteId}
                                                                onValueChange={(v) =>
                                                                    updateAssignment(attachmentId, { ...assignment, siteId: v })
                                                                }
                                                            >
                                                                <SelectTrigger className="h-8 text-xs">
                                                                    <SelectValue placeholder="Select Site *" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {sites.length > 1 && (
                                                                        <SelectItem value={ALL_SITES_VALUE}>
                                                                            All Sites (group offer)
                                                                        </SelectItem>
                                                                    )}
                                                                    {Array.from(new Map(sites.map(site => [site.name, site])).values()).map((site, index) => (
                                                                        <SelectItem key={`${site.id}-${index}`} value={site.id || ''}>
                                                                            {site.name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            {!assignment.siteId && (
                                                                <p className="text-xs text-red-500 mt-0.5">Required</p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <Select
                                                                value={assignment.retailerId}
                                                                onValueChange={(v) =>
                                                                    updateAssignment(attachmentId, { ...assignment, retailerId: v })
                                                                }
                                                            >
                                                                <SelectTrigger className="h-8 text-xs">
                                                                    <SelectValue placeholder="Select Retailer *" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {loadingRetailers ? (
                                                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                                                            Loading retailers...
                                                                        </div>
                                                                    ) : dbRetailers.length === 0 ? (
                                                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                                                            No retailers available
                                                                        </div>
                                                                    ) : (
                                                                        dbRetailers.map(r => (
                                                                            <SelectItem key={r.id} value={r.id}>
                                                                                <div className="flex items-center gap-2">
                                                                                    {r.logo && (
                                                                                        <img src={r.logo} alt={r.name} className="w-4 h-4 object-contain" />
                                                                                    )}
                                                                                    <span>{r.name}</span>
                                                                                </div>
                                                                            </SelectItem>
                                                                        ))
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                            {!assignment.retailerId && (
                                                                <p className="text-xs text-red-500 mt-0.5">Required</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        className="w-full h-7 text-xs mt-2"
                                                        onClick={() => tenderId && startSingleParsing(attachmentId, tenderId)}
                                                        disabled={
                                                            !tenderId ||
                                                            isParsing ||
                                                            !assignment.siteId ||
                                                            !assignment.retailerId ||
                                                            parsedOffers.some(p => p.attachmentId === attachmentId && (p.status === 'queued' || p.status === 'parsing' || p.status === 'completed' || p.status === 'reviewed'))
                                                        }
                                                        title={!assignment.siteId || !assignment.retailerId ? 'Please select both Site and Retailer to parse offer' : ''}
                                                    >
                                                        {parsedOffers.some(p => p.attachmentId === attachmentId && p.status === 'completed') ? (
                                                            <>
                                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                                Parsed
                                                            </>
                                                        ) : parsedOffers.some(p => p.attachmentId === attachmentId && (p.status === 'queued' || p.status === 'parsing')) ? (
                                                            <>
                                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                Parsing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Zap className="w-3 h-3 mr-1" />
                                                                Parse Offer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Parse All Button */}
                                    {(() => {
                                        // Check if all attachments have site and retailer assigned
                                        const allAssigned = Array.from(selectedAttachments.keys()).every(attachmentId => {
                                            const assignment = assignments.get(attachmentId);
                                            return assignment && assignment.siteId && assignment.retailerId;
                                        });
                                        const canParseAll = allAssigned && selectedAttachments.size > 0;

                                        return (
                                            <div className="space-y-2">
                                                {!allAssigned && selectedAttachments.size > 0 && (
                                                    <p className="text-xs text-red-500 text-center">
                                                        Please select both Site and Retailer for all attachments to parse offers
                                                    </p>
                                                )}
                                                <Button
                                                    className="w-full"
                                                    size="lg"
                                                    onClick={handleStartParsing}
                                                    disabled={isParsing || !canParseAll}
                                                    title={!canParseAll ? 'Please select both Site and Retailer for all attachments' : ''}
                                                >
                                                    {isParsing ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            Parsing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Zap className="h-4 w-4 mr-2" />
                                                            Parse All Offers ({selectedAttachments.size})
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Progress View */}
            {viewMode === 'progress' && (
                <div className="flex-1 overflow-y-auto">
                    <BatchParsingProgress
                        parsedOffers={parsedOffers}
                        overallProgress={overallProgress}
                        isParsing={isParsing}
                        onRetry={retryParsing}
                        onCancelOffer={cancelOffer}
                        onCancelAll={cancelAllParsing}
                    />
                </div>
            )}

            {/* Review View */}
            {viewMode === 'review' && (
                <div className="flex-1 flex flex-col gap-2 min-h-0 overflow-hidden">
                    {/* Stated once for the whole tender: which contract length each Year is.
                        One chip, not a block — Stage 3's height is spent on the offer list and
                        the rate table, and this is touched once per tender. */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
                        <TenderTermSeriesPanel
                            series={termSeries || {}}
                            onChange={setTermSeries}
                            proposed={suggestedTermSeries}
                            offerTerms={parsedOfferTermSummary.perOffer}
                            contractStart={parsedOfferTermSummary.contractStart}
                            offerStarts={parsedOfferTermSummary.startsByRetailer}
                            startsDisagree={parsedOfferTermSummary.startsDisagree}
                            retermOffers={retermOffers}
                            onApplyReterm={handleApplyReterm}
                            onRetermTerm={handleRetermSingleTerm}
                        />
                    </div>

                    <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0 overflow-hidden">
                    {/* Left: Offer List Sidebar - Responsive Width */}
                    <Card className="w-full lg:w-64 flex flex-col flex-shrink-0 h-64 lg:h-full overflow-hidden">
                        <CardHeader className="p-3 pb-2">
                            <CardTitle className="text-xs font-medium flex items-center justify-between">
                                <span>Offers</span>
                                <span className="text-[10px] text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded-full">
                                    {parsedOffers.filter(o => o.status === 'reviewed').length}/{parsedOffers.length}
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-2">
                            <div className="space-y-1.5">
                                {parsedOffers.map((offer, index) => {
                                    const isReviewed = offer.status === 'reviewed';
                                    const isActive = index === reviewOfferIndex;

                                    return (
                                        <div
                                            key={offer.id}
                                            onClick={async () => {
                                                // Flush whatever's currently being edited before
                                                // switching — the review screen resets its edit
                                                // state to the newly-selected offer's data with no
                                                // memory of what was in progress for this one.
                                                await reviewRef.current?.flushCurrentEdit();
                                                setReviewOfferIndex(index);
                                            }}
                                            className={`
                                                group relative p-2 rounded-md cursor-pointer border transition-all duration-200
                                                ${isActive
                                                    ? 'bg-primary/5 border-primary shadow-sm'
                                                    : 'hover:bg-gray-50 border-transparent hover:border-gray-200'
                                                }
                                                ${isReviewed && !isActive ? 'bg-green-50/50' : ''}
                                            `}
                                        >
                                            <div className="flex items-start gap-2">
                                                <div className={`mt-0.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                                                    {isReviewed ? (
                                                        <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                    ) : (
                                                        <FileText className="h-3.5 w-3.5" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-[11px] font-medium truncate ${isActive ? 'text-primary' : 'text-gray-700'}`}>
                                                        {offer.fileName}
                                                    </p>
                                                    {offer.assignment?.retailerId && (
                                                        <p className="text-[10px] text-blue-600 truncate mt-0.5">
                                                            {dbRetailers.find(r => r.id === offer.assignment.retailerId)?.name}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <span className={`text-[9px] px-1 py-0 rounded-sm border ${isReviewed
                                                            ? 'bg-green-100 text-green-700 border-green-200'
                                                            : 'bg-gray-100 text-gray-600 border-gray-200'
                                                            }`}>
                                                            {isReviewed ? 'Reviewed' : 'Pending'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isActive && (
                                                    <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-primary rounded-r-lg" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                        <div className="p-3 border-t bg-gray-50/50">
                            <Button
                                className="w-full h-8 text-xs"
                                disabled={!canProceedToNext()}
                                onClick={async () => {
                                    // Capture whatever's currently being edited in the review
                                    // screen (even if "Save & Approve" was never clicked for it)
                                    // BEFORE running the reconstruction, so it isn't silently lost.
                                    const flushed = await reviewRef.current?.flushCurrentEdit();
                                    handleProceedToOffers(flushed);
                                }}
                                variant={canProceedToNext() ? "default" : "secondary"}
                            >
                                {canProceedToNext() ? (
                                    <>
                                        <CheckCircle className="h-3 w-3 mr-1.5" />
                                        Finalize
                                    </>
                                ) : (
                                    <span>Review All</span>
                                )}
                            </Button>
                        </div>
                    </Card>

                    {/* Right: Review Component - Maximized Space */}
                    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto border rounded-lg">
                        <OfferReviewComparison
                            ref={reviewRef}
                            offers={parsedOffers}
                            currentIndex={reviewOfferIndex}
                            onNavigate={(index) => setReviewOfferIndex(index)}
                            onReparse={(offerId) => {
                                retryParsing(offerId);
                                setViewMode('progress');
                            }}
                            /**
                             * Navigating away saves the edit and nothing else.
                             *
                             * Keeps the edit in context AND in the parsed_offer_document row, so
                             * it survives a reload — but leaves `status` alone. Approving is the
                             * reviewer's act, not a side effect of clicking the next retailer.
                             * createOfferFromParsedDoc is deliberately NOT called here: that
                             * pushes the offer into Compare & Select, which is what approval is
                             * for.
                             */
                            onSaveEdits={async (offerId, editedData) => {
                                saveOfferEdits(offerId, editedData);
                                const offer = parsedOffers.find(o => o.id === offerId);
                                if (!offer || !tenderId || !editedData) return;
                                try {
                                    await emailApiService.saveParsedDocument({
                                        attachmentId: offer.attachmentId,
                                        tenderId,
                                        retailerId: offer.assignment.retailerId,
                                        tenderSiteId: offer.assignment.siteId,
                                        parsedData: editedData,
                                        confidenceScore: null,
                                        fileName: offer.fileName,
                                    });
                                } catch (error) {
                                    console.error('Failed to save in-progress edit:', error);
                                }
                            }}
                            onApprove={async (offerId, editedData) => {
                                // Find the offer being approved
                                const offer = parsedOffers.find(o => o.id === offerId);

                                // Mark as reviewed in context
                                markAsReviewed(offerId, editedData);

                                // Save parsed document to database
                                if (offer && tenderId) {
                                    try {
                                        const dataToSave = editedData || offer.parsedData;
                                        // Derive confidence from non-null fields in parsed data
                                        const parsedTermKeys = Object.keys(dataToSave?.contract_terms || dataToSave?.contractTerms || {}).filter(k => !isNaN(Number(k)));
                                        const parsedEnv = dataToSave?.smooth?.environmental_charges || dataToSave?.smooth?.environmentalCharges || {};
                                        const parsedLines = Object.values(dataToSave?.contract_terms || dataToSave?.contractTerms || {});
                                        const derivedScore = Math.min(100,
                                            (parsedTermKeys.length > 0 ? 40 : 0) +
                                            (parsedLines.length > 0 ? 30 : 0) +
                                            (Object.keys(parsedEnv).length > 0 ? 30 : 0)
                                        );
                                        const savedDoc = await emailApiService.saveParsedDocument({
                                            attachmentId: offer.attachmentId,
                                            tenderId: tenderId,
                                            retailerId: offer.assignment.retailerId,
                                            tenderSiteId: offer.assignment.siteId,
                                            parsedData: dataToSave,
                                            confidenceScore: derivedScore || null,
                                            fileName: offer.fileName
                                        });
                                        console.log(`✅ Saved parsed document to DB for: ${offer.fileName}`);

                                        // Push the reviewed/edited data into the actual offer record too —
                                        // saveParsedDocument only updates the parsed_offer_document audit row,
                                        // which Compare & Select never reads, so without this the user's edits
                                        // never reach the rest of the tender workflow.
                                        if (savedDoc?.id) {
                                            await emailApiService.createOfferFromParsedDoc(savedDoc.id);
                                            console.log(`✅ Synced offer from reviewed data for: ${offer.fileName}`);
                                        }
                                    } catch (error) {
                                        console.error('❌ Failed to save parsed document / sync offer to DB:', error);
                                        // Don't block the flow, just log the error
                                    }
                                }

                                toast({
                                    title: 'Offer Approved',
                                    description: 'Offer has been reviewed and saved',
                                    duration: 2000,
                                });

                                // Auto-advance to next unreviewed offer
                                const nextUnreviewedIndex = parsedOffers.findIndex((o, i) => i > reviewOfferIndex && o.status !== 'reviewed');
                                if (nextUnreviewedIndex !== -1) {
                                    setReviewOfferIndex(nextUnreviewedIndex);
                                } else {
                                    // Or just next if all subsequent are reviewed (wrapping or just next)
                                    if (reviewOfferIndex < parsedOffers.length - 1) {
                                        setReviewOfferIndex(reviewOfferIndex + 1);
                                    }
                                }
                            }}
                        />
                    </div>
                    </div>
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={onPrevious}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                </Button>
                <Button onClick={async () => {
                                    // Capture whatever's currently being edited in the review
                                    // screen (even if "Save & Approve" was never clicked for it)
                                    // BEFORE running the reconstruction, so it isn't silently lost.
                                    const flushed = await reviewRef.current?.flushCurrentEdit();
                                    handleProceedToOffers(flushed);
                                }} disabled={!canProceedToNext()}>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
};

export default EmailProcessingStep;
